/**
 * The builder canvas: widgets on a 12-column grid. Drag a widget to move it,
 * drag its corner to resize, or select it and use the keyboard (arrows move,
 * Shift+arrows resize, Delete removes). The drop position is previewed as a
 * ghost and settled by grid.ts, so overlap is impossible by construction.
 */

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';

import { clampBox, COLUMNS, fitsPage, gridHeight, ROW_HEIGHT, settle, type Box } from './grid.ts';
import type { CatalogueSource, Widget, WidgetResult } from './reports-api.ts';
import { FullScreenWidget } from './WidgetMenu.tsx';
import { WidgetView } from './WidgetView.tsx';

interface Props {
  widgets: Widget[];
  results: Record<string, WidgetResult>;
  sources: CatalogueSource[];
  editable: boolean;
  autoAlign: boolean;
  selected: number | null;
  onSelect: (index: number | null) => void;
  onChange: (widgets: Widget[]) => void;
  onRemove: (index: number) => void;
  /** Result key per widget index (stable ids for unsaved widgets). */
  keyOf: (index: number) => string;
  /** Report pages: fixed rows and a row height that keeps the page's shape. Dashboards omit both. */
  maxRows?: number | null;
  rowHeight?: number;
  /** Renders instead of widgets for cover and contents pages. */
  children?: ReactNode;
  /** The "+" under a widget: add a new one directly below it. */
  onAddBelow?: ((index: number) => void) | undefined;
  /** The widget's "⋯" menu (builder only); it can ask the canvas to open full screen. */
  renderMenu?: ((index: number, openFullScreen: () => void) => ReactNode) | undefined;
}

interface Drag {
  index: number;
  kind: 'move' | 'resize';
  startX: number;
  startY: number;
  origin: Box;
  preview: Box;
}

function useWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(960);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? 960));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

export function Canvas({
  widgets,
  results,
  sources,
  editable,
  autoAlign,
  selected,
  onSelect,
  onChange,
  onRemove,
  keyOf,
  maxRows = null,
  rowHeight,
  children,
  onAddBelow,
  renderMenu,
}: Props): ReactNode {
  const [fullScreen, setFullScreen] = useState<number | null>(null);
  const [ref, width] = useWidth();
  const ROW = rowHeight ?? ROW_HEIGHT;
  const [drag, setDrag] = useState<Drag | null>(null);
  const column = width / COLUMNS;

  useEffect(() => {
    if (!drag) return undefined;
    const move = (event: globalThis.PointerEvent) => {
      const dx = (event.clientX - drag.startX) / column;
      const dy = (event.clientY - drag.startY) / ROW;
      const widget = widgets[drag.index] as Widget;
      const box =
        drag.kind === 'move'
          ? { ...drag.origin, x: drag.origin.x + dx, y: drag.origin.y + dy }
          : { ...drag.origin, w: drag.origin.w + dx, h: drag.origin.h + dy };
      setDrag({ ...drag, preview: clampBox(box, widget.type) });
    };
    const up = () => {
      const next = settle(
        widgets.map((widget, index) =>
          index === drag.index ? { ...widget, ...drag.preview } : widget,
        ),
        drag.index,
        autoAlign,
      );
      // On a report page a move that would push anything off the page is refused.
      if (fitsPage(next, maxRows)) onChange(next);
      setDrag(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up, { once: true });
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [drag, widgets, column, autoAlign, onChange]);

  const startDrag = (index: number, kind: Drag['kind']) => (event: PointerEvent) => {
    if (!editable || event.button !== 0) return;
    if ((event.target as HTMLElement).closest('button, a, input, textarea, select')) return;
    event.preventDefault();
    const widget = widgets[index] as Widget;
    const origin = { x: widget.x, y: widget.y, w: widget.w, h: widget.h };
    onSelect(index);
    setDrag({ index, kind, startX: event.clientX, startY: event.clientY, origin, preview: origin });
  };

  const onKey = (index: number) => (event: KeyboardEvent) => {
    if (!editable) return;
    const widget = widgets[index] as Widget;
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      onRemove(index);
      return;
    }
    const step = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[
      event.key
    ];
    if (!step) return;
    event.preventDefault();
    const [dx, dy] = step as [number, number];
    const box = event.shiftKey
      ? { ...widget, w: widget.w + dx, h: widget.h + dy }
      : { ...widget, x: widget.x + dx, y: widget.y + dy };
    const next = settle(
      widgets.map((item, at) => (at === index ? { ...item, ...clampBox(box, widget.type) } : item)),
      index,
      autoAlign,
    );
    if (fitsPage(next, maxRows)) onChange(next);
  };

  const rows =
    maxRows ??
    Math.max(
      gridHeight(widgets) + (editable ? 3 : 0),
      drag ? drag.preview.y + drag.preview.h + 1 : 0,
      6,
    );

  return (
    <div
      ref={ref}
      className={`rb-canvas${editable ? ' rb-canvas-edit' : ''}${maxRows !== null ? ' rb-canvas-page' : ''}`}
      style={{
        height: rows * ROW,
        ['--col' as string]: `${column}px`,
        ['--row' as string]: `${ROW}px`,
      }}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onSelect(null);
      }}
    >
      {children}
      {drag && (
        <div
          className="rb-ghost"
          style={{
            left: drag.preview.x * column,
            top: drag.preview.y * ROW,
            width: drag.preview.w * column,
            height: drag.preview.h * ROW,
          }}
          aria-hidden="true"
        />
      )}
      {widgets.map((widget, index) => {
        const live = drag?.index === index ? { ...widget, ...drag.preview } : widget;
        const source = sources.find((item) => item.key === widget.data.source);
        const isSelected = selected === index;
        return (
          <div
            key={keyOf(index)}
            className={`rb-cell${isSelected ? ' rb-cell-on' : ''}${drag?.index === index ? ' rb-cell-drag' : ''} rb-cell-${widget.type.toLowerCase()}`}
            style={{
              left: live.x * column,
              top: live.y * ROW,
              width: live.w * column,
              height: live.h * ROW,
            }}
            tabIndex={editable ? 0 : -1}
            role={editable ? 'button' : undefined}
            aria-label={
              editable
                ? `${widget.type.toLowerCase().replace(/_/g, ' ')} widget, column ${widget.x + 1}, row ${widget.y + 1}. Arrows move, Shift+arrows resize, Delete removes.`
                : undefined
            }
            aria-pressed={editable ? isSelected : undefined}
            onPointerDown={startDrag(index, 'move')}
            onKeyDown={onKey(index)}
            onFocus={() => editable && onSelect(index)}
          >
            <div className="rb-cell-inner">
              <WidgetView
                widget={widget}
                result={results[keyOf(index)]}
                source={source}
                height={live.h * ROW - 16}
              />
            </div>
            {!editable && (
              <button
                type="button"
                className="rb-cell-zoom"
                aria-label="Full screen"
                title="Full screen"
                onClick={() => setFullScreen(index)}
              >
                ⤢
              </button>
            )}
            {editable && (
              <>
                {renderMenu?.(index, () => setFullScreen(index))}
                <button
                  type="button"
                  className="rb-cell-remove"
                  aria-label="Remove widget"
                  onClick={() => onRemove(index)}
                >
                  ×
                </button>
                {onAddBelow && (
                  <button
                    type="button"
                    className="rb-add-below"
                    aria-label="Add a widget below"
                    title="Add a widget below"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={() => onAddBelow(index)}
                  >
                    +
                  </button>
                )}
                <span
                  className="rb-resize"
                  onPointerDown={(event) => {
                    // The corner must not also start a move on the cell behind it.
                    event.stopPropagation();
                    startDrag(index, 'resize')(event);
                  }}
                  aria-hidden="true"
                />
              </>
            )}
          </div>
        );
      })}
      {fullScreen !== null && widgets[fullScreen] && (
        <FullScreenWidget
          widget={widgets[fullScreen] as Widget}
          result={results[keyOf(fullScreen)]}
          source={sources.find((item) => item.key === widgets[fullScreen]?.data.source)}
          onClose={() => setFullScreen(null)}
        />
      )}
    </div>
  );
}
